/**
 * Defines an alternative to dijit/layout/AccordionContainer.  This container differs from the dijit offering in that
 * it can display multiple children at once, rather than one at a time.  Other features include:
 * <ul>
 *    <li>ability to adjust child size by dragging the dividing lines between them</li>
 *    <li>ability to "collapse" inidividual children</li>
 *    <li>ability to reorder children by dragging their title bars
 *    <li>support for dragging children in and out of the container</li>
 *    <li>proportional resize of children upon container resize</li>
 *    <li>persistence support</li>
 * </ul>
 *
 * @module MW/layout/AccordionContainer
 * @copyright Copyright 2014-2020 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/_base/fx",
    "dojo/fx/easing",
    "dojo/on",
    "dojo/dom",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/Evented",
    "dojo/has",
    "dojo/keys",
    "dojo/mouse",
    "dojo/touch",
    "dijit/focus",
    "dijit/layout/_LayoutWidget",
    "MW/layout/AnimationTiming",
    "MW/layout/HidingContainer",
    "MW/layout/LayoutChildProperty",
    "MW/layout/LayoutContainerEvent",
    "MW/layout/LayoutUtils",
    "MW/layout/MaximizingContainer",
    "MW/layout/Restorable",
    "MW/layout/SelectionBlocker",
    "MW/layout/SelectionManager",
    "MW/layout/Traversable",
    "MW/layout/TitleBar"
], function (declare, event, lang, fx, easing, on, dom, domClass, domConstruct, domGeometry, domStyle,
             Evented, has, keys, mouse, touch, focus, _LayoutWidget, AnimationTiming, HidingContainer, LayoutChildProperty,
             LayoutContainerEvent, LayoutUtils, MaximizingContainer, Restorable, SelectionBlocker, SelectionManager,
             Traversable, TitleBar) {

    var COLLAPSE_THRESHOLD = 25;
    var DEFAULT_MINIMUM_SIZE = 40;


    /**
     * @class MW/layout/AccordionContainer
     * @classdesc Defines a container that displays its children in a vertical stack.  The vertical real-estate allocated
     *            to each child can be controlled by dragging boundaries and/or collapsing individual children.
     */
    var AccordionContainer = declare([_LayoutWidget, Evented, Restorable, Traversable, HidingContainer, MaximizingContainer], {
        /**
         * @property {boolean} animationEnabled - true if collapse/expand/restore operations should be animated.
         * @property {boolean} autoCollapseEnabled - true if panels should automatically collapse when sized below their minimum height
         * @property {boolean} dragOutEnabled - true if the user should be able to drag panels out of the container.
         *                                      This property is false by default but is automatically set to true if a
         *                                      LayoutContainerEvent.DRAG_OUT listener is attached
         * @instance
         */
        _: undefined,  // This is here just to trick the JSDoc parser into presenting the above properties in a single
        // table without making it look they are children of the first "member" below

        animationEnabled: true,
        autoCollapseEnabled: true,
        dragOutEnabled: false,
        baseClass: "accordionContainer",

        /* _panelInfo: array that stores the following per child panel:
         widget
         panelId
         properties
         title
         minSize
         maxSize
         isCollapsible
         closable
         portion
         isCollapsed
         explicitlyCollapsed
         explicitlyRestored
         distributeUnusedHeight
         titleBar
         splitterNode
         animationStartTime
         animationDuration
         animationStartHeight
         animationHeightChange
         animationStartTop
         animationStopBox
         moveBackground
         */

        /* _splitterDragInfo: object that stores the following information while a splitter drag is in progress
         panelInfo
         startY
         initialPosition
         splitterPosition
         initialHeights
         initialCollapsedStates
         proportionalResize
         lowerLimit
         upperLimit
         connections
         hasMoved
         */

        /* _titleDragInfo: object that stores the following information while title bar drag is in progress
         panelInfo
         startY
         startX
         lastY
         initialIndex
         currentIndex
         panelHeight
         initialTops
         connections
         hasMoved
        */

        // _splitterThickness
        // _visibleSplitterThickness
        // _lastLayoutHeight
        // _animationTimer
        // _animationDoneResponse
        // _pendingState
        // _portionsAreStale

        constructor: function () {
            this._panelInfo = [];

            // Property to keep track of extra navigation targets in the parent container to handle navigation
            // between AccordionContainer panels and them.
            this._extraNavigationTargets = [];

            // Flag used to block next child selection when a panel is collapsed/expanded using keyboard.
            this._shouldBlockSuccession = false;
        },

        buildRendering: function () {
            this.inherited(arguments);
            domClass.add(this.domNode, "mwLayoutContainer");
        },

        addExtraNavigationTarget: function (target) {
            this._extraNavigationTargets[this._extraNavigationTargets.length] = target;
        },

        startup: function () {
            if (this._started) {return; }

            var that = this;
            this.getChildren().forEach(function (child) {
                that._setupChild(child);
            });

            // Adding event listeners on the extra navigation targets
            for (var i = 0; i < this._extraNavigationTargets.length; i++) {
                this.own(on(this._extraNavigationTargets[i], "keydown", lang.hitch(this, "_keydownHandler", undefined)));
            }

            if (this.getParent) {
                var parent = this.getParent();
                while (parent && !parent.selectionManager) {
                    parent = parent.getParent();
                }
                if (parent && parent.selectionManager) {
                    this.selectionManager = parent.selectionManager;
                }
            }

            this.inherited(arguments);

            if (this._pendingState && this._isPopulatedPerState(this._pendingState)) {
                this._restoreState(this._pendingState);
                delete this._pendingState;
                this.emit(LayoutContainerEvent.STATE_CHANGED);
            }
        },

        /**
         * Adds a child to the container.  The child will be managed in accordance with the provided properties.
         * If the properties object is Stateful then the container will update the values of properties designated
         * as "dynamic" below.  It will also respond to changes made to these properties.
         * Hence, for example, if the user collapses a child the corresponding isCollapsed property will be set
         * true by the container.  Conversely if the some code outside the container sets the
         * isCollapsed property true the container will respond by collapsing the corresponding child.
         *
         * @param widget {_Widget} the child to add
         * @param properties {Object} an object containing any of the following properties:
         * @param properties.paneId (string)- Identifies the child for purposes of state save and restore
         * @param properties.title {string} - Displayed on title bar above child (this property is dynamic)
         * @param properties.closable (boolean) - Determines whether the child will be "closed" in response to middle click on its title bar
         * @param properties.isCollapsible {boolean} - Determines whether the child can be "collapsed", such that only its title bar appears
         * @param properties.isCollapsed {boolean} true if the child is collapsed (this property is dynamic)
         * @param properties.isResizable {boolean} true of the user should be able to adjust the childs height by dragging
         * @param properties.preferredHeight {number} - Specifies the height initially allocated to the child.  If the value is > 0 and < 1 it
         *                                              will be interpreted as a fraction of the available height.  If it is > 1 it will be
         *                                              interpreted as a height in pixels.  Finally, if it is <= 0 the height will be
         *                                              obtained from the child's computed style.  If no preferredHeight is specified
         *                                              the initial height will be 1/Nth of the available height where N is the number
         *                                              of children.
         * @param properties.minSize {Object} - Lower bound on the child's "free" (within the container) dimension in pixels.
         *        This can be a number or a function that returns a number.
         * @param properties.maxSize {Object} - Upper bound on the child's "free" (within the container) dimension in pixels
         *        This can be a number or a function that returns a number.
         * @param index {number} the relative position of the child within the container (this property is dynamic)
         *
         * @instance
         */
        addWithProperties: function (widget, properties, index) {
            var panelInfo = this._createPanelInfo(widget);
            if (properties !== widget) {
                lang.mixin(panelInfo, properties);
            } else {
                this._getInfoFromWidget(widget, panelInfo);
            }
            this._bindChildProperties(panelInfo, properties);
            if (properties.isCollapsible === undefined) {
                panelInfo.isCollapsible = !!properties.title;
            }
            if (index === undefined || index < 0 || index > this._panelInfo.length) {
                this._panelInfo.push(panelInfo);
            } else {
                this._panelInfo.splice(index, 0, panelInfo);
            }
            this.addChild(widget);
        },

        /**
         * This is an alternative to the addWithProperties function above.  It assumes any relevant properties have
         * been added directly to the widget object.
         *
         * This method overrides _LayoutWidget.addChild
         *
         * @param widget {_Widget} the child to add
         * @param index {number} the relative position of the child within the container
         * @instance
         */
        addChild: function (widget, index) {

            var panelInfo = this._getPanelInfoFromDomId(widget.id);
            if (!panelInfo) {
                panelInfo = this._createPanelInfo(widget);
                if (index === undefined || index < 0 || index > this._panelInfo.length) {
                    this._panelInfo.push(panelInfo);
                } else {
                    this._panelInfo.splice(index, 0, panelInfo);
                }
                this._getInfoFromWidget(widget, panelInfo);
            } else if (!widget.title) {
                widget.title = panelInfo.title;
            }
            this._bindChildProperties(panelInfo, widget);
            // If a preferredHeight between 0 and 1 has been specified, interpret it as a portion
            if (panelInfo.preferredHeight !== undefined && panelInfo.preferredHeight > 0 && panelInfo.preferredHeight < 1) {
                panelInfo.portion = panelInfo.preferredHeight;
            }
            this.inherited(arguments);
            this._updateCollapseControlVisibility();
            this.emit(LayoutContainerEvent.CHILD_ADDED, panelInfo.widget, panelInfo.properties);
            if (this._started) {
                if (this._pendingState && this._isPopulatedPerState(this._pendingState)) {
                    this._restoreState(this._pendingState);
                    delete this._pendingState;
                } else {
                    this.layout();
                }
                this.emit(LayoutContainerEvent.STATE_CHANGED);
            }
            this._updatePanelIndices();
        },

        _setupChild: function (child) {
            var panelInfo, lineNode, title, titleButtonId;

            this.inherited(arguments);

            domStyle.set(child.domNode, "position", "absolute");
            panelInfo = this._getPanelInfoFromDomId(child.id);
            if (panelInfo && !panelInfo.childWasSetup) {
                panelInfo.childWasSetup = true;  // Prevent the following from being repeated, for example if child widget
                // is borrowed and returned (see LendingAccordionContainer)

                // If a preferred height was specified store the initial height, computing if necessary
                if (panelInfo.preferredHeight !== undefined) {
                    if (panelInfo.preferredHeight > 1) {
                        panelInfo.initialHeight = panelInfo.preferredHeight;
                    } else if (panelInfo.preferredHeight <= 0) {
                        panelInfo.initialHeight = domStyle.get(child.domNode, "height");
                    }
                }

                // Create splitterNode and titleNode as applicable
                panelInfo.splitterNode = domConstruct.create("div", {id : this.id + child.id + "Splitter"}, this.domNode);
                domClass.add(panelInfo.splitterNode, "splitter");
                dom.setSelectable(panelInfo.splitterNode, false);
                LayoutUtils.preventFocus(panelInfo.splitterNode, true);
                if (!this._splitterThickness) {
                    this._splitterThickness = Math.round(domStyle.get(panelInfo.splitterNode, "height"));
                }
                if (this._panelInfo.indexOf(panelInfo) === 0) {
                    domStyle.set(panelInfo.splitterNode, "display", "none");
                }
                title = panelInfo.properties.title;
                if (title) {
                    // Add a title bar.
                    panelInfo.titleBar = new TitleBar({id : child.id + "TitleBar",
                        properties: panelInfo.properties,
                        title: title,
                        isCollapsible: panelInfo.isCollapsible && this._panelInfo.length > 1,
                        isMaximizable: panelInfo.properties.isMaximizable});
                    domConstruct.place(panelInfo.titleBar.domNode, this.domNode);

                    // Connect listeners to support collapse as well as reordering via drag
                    panelInfo.titleBar.connections = [
                        on(panelInfo.titleBar.toggleNode, "click", lang.hitch(this, "_toggleCollapse", panelInfo)),
                        on(panelInfo.titleBar.domNode, touch.press, lang.hitch(this, "_beginTitleDrag", panelInfo))
                    ];

                    panelInfo.titleBar.titleButton.setAttribute("tabindex", -1);
                    panelInfo.widget.navAssociation = panelInfo.titleBar.titleButton;

                    // Flag to let UIContainer know that it should prevent focusing the content of the
                    // AccordionContainer while navigating with keyboard using Ctrl + F6.
                    panelInfo.widget.preventContentFocusOnKeyNav = true;

                    // For remembering the correct navAssociation when the side container is collapsed and then
                    // restored since for collapsed panel the navAssociation is the button and for restored panel,
                    // it is the title bar.
                    panelInfo.widget.sideBarExpandedNavAssociation = panelInfo.titleBar.titleButton;

                    panelInfo.titleBar.titleButton.setAttribute("aria-controls", panelInfo.widget.id);
                    titleButtonId = panelInfo.widget.id + "TitleButton";
                    panelInfo.titleBar.titleButton.setAttribute("id", titleButtonId);
                    panelInfo.widget.domNode.setAttribute("aria-labelledby", titleButtonId);
                    panelInfo.widget.domNode.setAttribute("role", "region");

                    this.own(on(panelInfo.titleBar.domNode, "keydown", lang.hitch(this, "_keydownHandler", panelInfo)));
                    this.own(on(panelInfo.titleBar.titleButton, "keyup", lang.hitch(this, "_keyUpHandler", panelInfo)));

                    // focusWithinTitleBar class is used to style the header appropriately based on the titleButton
                    // and actionsNodeWrapper gets focus or not.
                    this.own(on(panelInfo.titleBar.titleButton, "focus", function () {
                        panelInfo.titleBar.domNode.classList.add("focusWithinTitleBar");
                    }));
                    this.own(on(panelInfo.titleBar.titleButton, "blur", function () {
                        panelInfo.titleBar.domNode.classList.remove("focusWithinTitleBar");
                    }));
                    this.own(on(panelInfo.titleBar.actionsNodeWrapper, "focus", function () {
                        panelInfo.titleBar.domNode.classList.add("focusWithinTitleBar");
                    }));
                    this.own(on(panelInfo.titleBar.actionsNodeWrapper, "blur", function () {
                        panelInfo.titleBar.domNode.classList.remove("focusWithinTitleBar");
                    }));
                }
                // Add a line to the, otherwise invisible, splitter
                lineNode = domConstruct.create("div", {id : child.domNode.id + "SplitterLine"}, panelInfo.splitterNode);
                domClass.add(lineNode, "splitterLine");
                dom.setSelectable(lineNode, false);
                LayoutUtils.preventFocus(lineNode, true);
                if (!this._visibleSplitterThickness) {
                    this._visibleSplitterThickness = Math.round(domStyle.get(lineNode, "height"));
                    this._splitterOverlap          = Math.round(domStyle.get(panelInfo.splitterNode, "padding-top"));
                }

                // Connect listeners to support splitter drag
                var beginDragFunction = lang.hitch(this, "_beginSplitterDrag", panelInfo);
                var suppressContextMenuFunction = function (event) {event.preventDefault(); };
                on(panelInfo.splitterNode, touch.press, beginDragFunction);
                on(panelInfo.splitterNode, "contextmenu", suppressContextMenuFunction);
            }
        },

        /**
         * This method overrides _LayoutWidget.removeChild
         *
         * @param widget {_Widget} the widget to remove
         * @instance
         */
        removeChild: function (widget) {
            var panelIndex = this._panelIndexFromDomId(widget.id);
            var panelInfo, removedProperties;
            if (panelIndex >= 0) {
                panelInfo = this._panelInfo[panelIndex];
                if (this._titleDragInfo) {
                    this._endTitleDrag(true);
                }

                if (panelInfo.splitterNode) {
                    domConstruct.destroy(panelInfo.splitterNode);
                }
                if (panelInfo.titleBar) {
                    panelInfo.titleBar.destroyRecursive();
                }
                if (panelInfo.properties) {
                    removedProperties = panelInfo.properties;
                    panelInfo.unbindChildProperties();
                }
                this._panelInfo.splice(panelIndex, 1);

                this.inherited(arguments);
                if (removedProperties) {
                    removedProperties.set(LayoutChildProperty.IS_COLLAPSED, false);
                    removedProperties.set(LayoutChildProperty.IS_EXPANDED, false);
                }
                this._updateCollapseControlVisibility();
                this._updatePanelIndices();

                this.emit(LayoutContainerEvent.CHILD_REMOVED, widget, removedProperties);
                if (this._started) {
                    this.layout();
                    this.emit(LayoutContainerEvent.STATE_CHANGED);
                }
                // Clean up any style changes we made to the child
                LayoutUtils.restoreStyle(widget);
            }
        },

        /**
         * Returns an array containing all of the "panels" that have been added to the container.  Note that this
         * differs from the array returned by the inherited getChildren method because the later will include
         * title bars.
         *
         * @returns {number} the number of panels
         * @instance
         */
        getPanels: function () {
            var i;
            var panels = [];
            panels.length = this._panelInfo.length;
            for (i = 0; i < this._panelInfo.length; i++) {
                panels[i] = this._panelInfo[i].widget;
            }
            return panels;
        },

        /**
         * @returns {Array} containing the properties associated with each panel
         * @instance
         */
        getPanelProperties: function () {
            var i;
            var properties = [];
            properties.length = this._panelInfo.length;
            for (i = 0; i < this._panelInfo.length; i++) {
                properties[i] = this._panelInfo[i].properties;
            }
            return properties;
        },

        /**
         * @returns {boolean} true if the container has no children
         * @instance
         */
        isEmpty: function () {
            return this._panelInfo.length === 0;
        },

        /**
         * Removes (and optionally destroys) all children
         *
         * @param destroy {boolean} true
         * @instance
         */
        removeAll: function (destroy) {
            var child;
            while (this._panelInfo.length > 0) {
                child = this._panelInfo[this._panelInfo.length - 1].widget;
                this.removeChild(child);
                if (destroy) {
                    child.destroyRecursive();
                }
            }
        },

        /**
         * Overrides method inherited from Evented to enable drag out when a LayoutContainerEvent.DRAG_OUT listener is attached
         *
         * @param type {string} the name of the event to listen for
         * @instance
         */
        on: function (type) {
            if (type === LayoutContainerEvent.DRAG_OUT) {
                this.dragOutEnabled = true;
            }
            this.inherited(arguments);
        },

        /**
         * Collapses a panel.  The actual panel widget may be passed or the panel may be identified
         * by its panelId, title or index within the container.
         *
         * @param widgetPanelIdTitleOrIndex {Object} identifies the child to be collapsed
         * @instance
         */
        collapse: function (widgetPanelIdTitleOrIndex) {
            var index = this._getPanelIndex(widgetPanelIdTitleOrIndex);
            if (index >= 0) {
                this._collapse(this._panelInfo[index]);
            }
        },

        /**
         * Returns true if the specified panel is currently collapsed.
         *
         * @param widgetPanelIdTitleOrIndex {Object} identifies the child to be collapsed
         * @instance
         */
        isCollapsed: function (widgetPanelIdTitleOrIndex) {
            var index = this._getPanelIndex(widgetPanelIdTitleOrIndex);
            return index >= 0 && this._panelInfo[index].isCollapsed;
        },

        /**
         * Expands a panel.  Expanding a panel is equivalent to collapsing all other panels in the container.
         * The actual panel widget may be passed or the panel may be identified by its panelId, title or index within the container.
         *
         * @param widgetPanelIdTitleOrIndex {Object} identifies the child of interest
         * @instance
         */
        expand: function (widgetPanelIdTitleOrIndex) {
            var index = this._getPanelIndex(widgetPanelIdTitleOrIndex);
            if (index >= 0) {
                this._expand(this._panelInfo[index]);
            }
        },

        /**
         * Returns true if the specified panel is currently expanded.
         *
         * @param widgetPanelIdTitleOrIndex {Object} identifies the child of interest
         * @instance
         */
        isExpanded: function (widgetPanelIdTitleOrIndex) {
            var index = this._getPanelIndex(widgetPanelIdTitleOrIndex);
            return index >= 0 && this._panelInfo[index] === this._expandedInfo;
        },

        /**
         * Restores a collapsed or expanded panel.  The actual panel widget may be passed or the panel may be identified
         * by its panelId, title or index within the container.
         *
         * @param widgetPanelIdTitleOrIndex {Object} identifies the child to be collapsed
         * @instance
         */
        restore: function (widgetPanelIdTitleOrIndex) {
            var index = this._getPanelIndex(widgetPanelIdTitleOrIndex);
            if (index >= 0) {
                this._restore(this._panelInfo[index]);
            }
        },

        /**
         * This is part of the Traversable API.
         *
         * @param widget {_Widget} the widget to reveal
         * @instance
         */
        revealChild: function (widget) {
            if (this.isCollapsed(widget)) {
                this.restore(widget);
            }
        },

        /**
         * Determines the next, in top to bottom order, child relative to the given reference child.  This is part of the Traversable API.
         *
         * @param widget {_Widget} the reference child widget
         * @param showing {boolean} if true then return a child that is already showing
         * @returns {_Widget} the next child widget
         * @instance
         */
        nextChild: function (widget, showing) {
            var panelCount = this._panelInfo.length;
            if (!panelCount) {return null;}
            var index = widget ? this._getPanelIndex(widget) : -1;

            if (index === -1) {
                for (var i = 0; i < this._panelInfo.length; i += 1) {
                    // Find the widget that is selected and update the widget and index to ensure that we get the
                    // correct next child.
                    if (this._panelInfo[i].widget && this._panelInfo[i].properties
                        && this._panelInfo[i].properties.get(LayoutChildProperty.IS_SELECTED) === true) {
                        widget = this._panelInfo[i].widget;
                        index = this._getPanelIndex(widget);
                    }
                }
            }
            var nextInfo;
            for (index++; index < panelCount; index++) {
                nextInfo = this._panelInfo[index];
                // Check to see if the nextChild is not already selected. An ideal use case for this is when there is only
                // one child within an AccordionContainer.
                if ((!showing || !nextInfo.isCollapsed) && nextInfo.widget && nextInfo.properties
                    && !nextInfo.properties.get(LayoutChildProperty.IS_SELECTED)) {
                    return nextInfo.widget;
                }
            }
            return null;
        },

        /**
         * Determines the previous, in top to bottom order, child relative to the given reference child.  This is part of the Traversable API.
         *
         * @param widget {_Widget} the reference child widget
         * @param showing {boolean} if true then return a child that is already showing
         * @returns {_Widget} the previous child widget
         * @instance
         */
        previousChild: function (widget, showing) {
            var panelCount = this._panelInfo.length;
            if (!panelCount) {return null;}
            var index = widget ? this._getPanelIndex(widget) : panelCount;
            var prevInfo;
            for (index--; index >= 0; index--) {
                prevInfo = this._panelInfo[index];
                if (!showing || !prevInfo.isCollapsed) {
                    return prevInfo.widget;
                }
            }
            return null;
        },

        /** This fulfills the HidingContainer interface
         * @instance
         */
        isShowingLocally: function (widget) {
            return widget.getParent() === this && !this.isCollapsed(widget);
        },

        /*
         * Part of the MaximizingContainer interface
         */
        canMaximizeLocally: function (child) {
            if (this._panelInfo.length < 2) {
                return false;
            }
            var index = this._getPanelIndex(child);
            return index >= 0 && this._panelInfo[index] !== this._expandedInfo &&
                                 this._panelInfo[index] !== this._expandingInfo;
        },

        /*
         * Part of the MaximizingContainer interface
         */
        canRestoreLocally: function (child) {
            var index = this._getPanelIndex(child);
            return index >= 0 && (this._panelInfo[index] === this._expandedInfo ||
                                  this._panelInfo[index] === this._expandingInfo);
        },

        /*
         * Part of the MaximizingContainer interface
         */
        maximizeLocally: function (child) {
            this.expand(child);
        },

        /*
         * Part of the MaximizingContainer interface
         */
        restoreLocally: function (child) {
            this.restore(child);
        },

        /**
         * Sets the fraction of the container height allocated to each panel.
         *
         * @param portions {Array} of numbers
         * @instance
         */
        setPortions: function (portions) {
            var count = Math.min(portions.length, this._panelInfo.length);
            var index;
            for (index = 0; index < count; index += 1) {
                this._panelInfo[index].portion = portions[index];
            }
            this.layout();
        },

        /**
         * Returns an object that encapsulates the container's user modifiable state, including the
         * ordering of panels, panel collapse states and positioning of the splitters between panels.
         * This object can subsequently be passed to the setState function in order to restore the container
         * to a previous state.  This is part of the Restorable API.
         *
         * @returns {Object} object encapsulating the current state
         * @instance
         */
        getState: function () {
            var state = [];
            state.length = this._panelInfo.length;
            var index, panelInfo;
            for (index = 0; index < this._panelInfo.length; index += 1) {
                panelInfo = this._panelInfo[index];
                state[index] = {
                    id : panelInfo.panelId,
                    collapsed : !!panelInfo.explicitlyCollapsed
                };
                if (panelInfo.portion !== undefined) {
                    state[index].portion = panelInfo.portion;
                }
            }
            return state;
        },

        /**
         * Restores the container to a previously saved state (see getState).  If the container does not currently have
         * the same children it had when the state was captured the actual restoration will be deferred until such time
         * as any missing children have been added.  This is part of the Restorable API.
         *
         * @param state {Object} object encapsulating previous state
         * @instance
         */
        setState: function (state) {
            if (this._isPopulatedPerState(state)) {
                this._restoreState(state);
            } else {
                this._pendingState = state;
            }
        },

        /**
         * This is part of the Restorable API.
         *
         * @returns {boolean} true if the container is in the process of restoring state.
         * @instance
         */
        isRestoringState: function () {
            return this._pendingState !== undefined;
        },

        /**
         * Ends the restore process initiated by a previous setState call.  endStateRestore is called automatically when
         * restoration is complete but may also be called explicitly to prematurely terminate restoration.
         *
         * This is part of the Restorable API.
         *
         * @instance
         */
        endStateRestore: function () {
            delete this._pendingState;
        },

        /**
         * Returns true if the container's state is being restored and the specified widget (identified by its panelId)
         * is included in the state being rerstored.
         *
         * @param panelId {string} id idendifying widget of interest
         * @returns {boolean} true if the specified child is expected
         * @instance
         */
        isExpecting: function (panelId) {
            return this._pendingState && AccordionContainer.stateIncludes(this._pendingState, panelId);
        },

        /**
         * Updates the container in response to resize.
         *
         * This method overrides the inherited resize method
         *
         * @instance
         */
        resize: function () {
            // Cache the computed style
            if (!this.computedStyle) {
                this.computedStyle = domStyle.getComputedStyle(this.domNode);
            }
            this.inherited(arguments);
        },

        /**
         * Positions the child widgets
         *
         * This method overrides the inherited layout method
         *
         * @instance
         */
        layout: function () {
            if (!this._started || !this.domNode) {
                return;
            }
            var childCount = this._panelInfo.length;
            var i, childBox, splitBox, childInfo, shouldCollapse, continueAnimating, finishedCollapse, finishedRestore;
            var currentTime = new Date().valueOf();
            var elapsedTime;
            // Suppress layout while a panel is being relocated by via drag
            if (childCount === 0 || this._titleDragInfo) {
                return;
            }

            var contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
            if (!contentBox || contentBox.h === 0) {
                // This happens, for example, if the AccordionContainer is within a SideContainer that is itself
                // collapsed  (see geck 1847964).
                this._terminateAnimations();
                return;  // No height
            }
            // If the portions are stale (meaning a collapse or restore occurred and was not followed by
            // updating of the portions) and the height is changing then update to portions now before
            // calculating the new heights.  If this is not done then resize can result in abrupt height
            // changes that reflect stale portions.
            if (this._portionsAreStale && this._lastLayoutHeight !== contentBox.h) {  // TODO: Not if animating or collapsing
                this._updatePortions(this._lastLayoutHeight);
            }

            // Calculate the panel heights, if the container hasn't grown taller since the last layout
            // disallow the restoring of panels that were automatically collapsed.
            var heights = this._calculateAllHeights(contentBox.h <= this._lastLayoutHeight);

            // See if any panel that that is not already restoring or collapsing needs to be
            var anyCollapseChange = false;
            for (i = 0; i < childCount && !anyCollapseChange; i++) {
                childInfo = this._panelInfo[i];
                shouldCollapse = heights[i] === 0;
                if ((shouldCollapse && !childInfo.isCollapsed && !this._isCollapsing(childInfo)) ||
                    (!shouldCollapse && childInfo.isCollapsed && !this._isRestoring(childInfo))) {
                    anyCollapseChange = true;
                }
            }

            // Initialize or update animations if a panel collapse state changed.
            if (anyCollapseChange && this.animationEnabled && LayoutUtils.isAnimationEnabled()) {
                for (i = 0; i < childCount; i++) {
                    childInfo = this._panelInfo[i];
                    shouldCollapse = heights[i] === 0;
                    // Animate height changes to any panel that is not collapsed and staying that way
                    if (!(shouldCollapse && childInfo.isCollapsed)) {
                        childInfo.animationStartHeight = childInfo.isCollapsed ? 0 : domGeometry.getMarginBox(childInfo.widget.domNode).h;
                        childInfo.animationHeightChange = heights[i] - childInfo.animationStartHeight;
                        childInfo.animationStartTime = currentTime;
                        childInfo.animationDuration  = AnimationTiming.computeDuration(childInfo.animationHeightChange);
                    }
                }
                // Start the animation timer if necessary
                if (!this._animationTimer) {
                    this._animationTimer = setInterval(lang.hitch(this, "layout"), AnimationTiming.UPDATE_INTERVAL);
                }
            }

            childBox = lang.clone(contentBox);
            continueAnimating = false;
            for (i = 0; i < childCount; i++) {
                childInfo = this._panelInfo[i];

                splitBox = lang.clone(childBox);
                splitBox.t -= childInfo.titleBar ? (this._splitterThickness / 2) : this._splitterOverlap;
                splitBox.h = this._splitterThickness;
                domGeometry.setMarginBox(childInfo.splitterNode, splitBox);
                if (i > 0) {
                    childBox.t += childInfo.titleBar ? 0 : this._visibleSplitterThickness;
                }
                if (childInfo.titleBar) {
                    childBox.h = TitleBar.getPreferredHeight();
                    domGeometry.setMarginBox(childInfo.titleBar.domNode, childBox);
                    childBox.t += childBox.h;
                }
                if (childInfo.animationStartTime) {
                    elapsedTime = currentTime - childInfo.animationStartTime;
                    if (elapsedTime >= childInfo.animationDuration) {
                        delete childInfo.animationStartTime;
                        delete childInfo.animationDuration;
                        delete childInfo.animationStartHeight;
                        delete childInfo.animationHeightChange;
                    } else {
                        continueAnimating = true;
                        heights[i] = childInfo.animationStartHeight + childInfo.animationHeightChange *
                            easing.cubicInOut(elapsedTime / childInfo.animationDuration);
                    }
                }
                if (heights[i] === 0) {
                    if (!childInfo.isCollapsed) {
                        this._finishCollapse(childInfo);
                        finishedCollapse = true;
                    }
                } else if (childInfo.isCollapsed) {
                    this._finishRestore(childInfo);
                    if (this._animationTimer) {
                        this._animatingRestore = true;
                    } else {
                        finishedRestore = true;
                    }
                }
                if (!childInfo.isCollapsed) {
                    childBox.h = heights[i];
                    LayoutUtils.setSize(childInfo.widget, childBox);
                    if (this._splitterDragInfo && childInfo.initialHeight && childInfo.initialHeight !== heights[i]) {
                        childInfo.userAdjustedHeight = true;
                    }
                    childBox.t += childBox.h;
                    if (!childInfo.animationStartTime || childInfo.animationHeightChange > 0) {
                        // Not collapsing
                        childInfo.lastRestoredHeight = childBox.h;
                    }
                }
            }
            if (this._animationTimer && !continueAnimating) {
                if (this._animatingRestore) {
                    finishedRestore = true;
                    delete this._animatingRestore;
                }
                clearInterval(this._animationTimer);
                delete this._animationTimer;
                if (this._animationDoneResponse) {
                    this._animationDoneResponse();
                    delete this._animationDoneResponse;
                }
            }

            if (!this._animationTimer && this._expandingInfo) {
                // Expand has completed
                this._expandedInfo = this._expandingInfo;
                delete this._expandingInfo;
                this._expandedInfo.properties.set(LayoutChildProperty.IS_EXPANDED, true);
            } else if (this._expandedInfo) {
                // If any collapsible panels are not collapsed then the panel is no longer expanded
                var anyNotCollapsed = false;
                for (i = 0; i < this._panelInfo.length && !anyNotCollapsed; i++) {
                    var info = this._panelInfo[i];
                    anyNotCollapsed = info !== this._expandedInfo && info.isCollapsible && !info.isCollapsed;
                }
                if (anyNotCollapsed || this._expandedInfo.isCollapsed) {
                    var expandedProperties = this._expandedInfo.properties;
                    delete this._expandedInfo;
                    if (anyNotCollapsed && this._panelsCollapsedPriorToExpand) {
                        delete this._panelsCollapsedPriorToExpand;
                    }
                    expandedProperties.set(LayoutChildProperty.IS_EXPANDED, false);
                }
            }

            this._lastLayoutHeight = contentBox.h;
            if (finishedCollapse || finishedRestore) {
                this.emit(LayoutContainerEvent.STATE_CHANGED);
            }
        },

        _terminateAnimations: function() {
            if (this._animationTimer) {

                clearInterval(this._animationTimer);
                delete this._animationTimer;

                for (var i = 0; i < this._panelInfo.length; i++) {
                    var panelInfo = this._panelInfo[i];
                    if (this._isCollapsing(panelInfo)) {
                        this._finishCollapse(panelInfo);
                    } else if (this._isRestoring(panelInfo)) {
                        this._finishRestore(panelInfo);
                    }
                    delete panelInfo.animationStartTime;
                    delete panelInfo.animationDuration;
                    delete panelInfo.animationStartHeight;
                    delete panelInfo.animationHeightChange;
                }
                if (this._animationDoneResponse) {
                    this._animationDoneResponse();
                    delete this._animationDoneResponse;
                }
            }
        },

        _createPanelInfo: function (widget) {
            return {
                widget          : widget,
                isCollapsible   : true,
                isResizable     : true,
                minSize         : DEFAULT_MINIMUM_SIZE,
                maxSize         : Number.MAX_VALUE
            };
        },

        _getInfoFromWidget: function (widget, panelInfo) {
            panelInfo.panelId = widget.panelId;
            if (widget.title !== undefined) {
                panelInfo.isCollapsible = true;
            }
            if (widget.isCollapsible !== undefined) {
                panelInfo.isCollapsible = widget.isCollapsible;
            }
            if (widget.closable !== undefined) {
                panelInfo.closable = widget.closable;
            }
            if (widget.portion !== undefined) {
                panelInfo.portion = widget.portion;
            }
            if (widget.minSize !== undefined) {
                panelInfo.minSize = widget.minSize;
            }
            if (widget.maxSize !== undefined) {
                panelInfo.maxSize = widget.maxSize;
            }
            if (widget.preferredHeight !== undefined) {
                panelInfo.preferredHeight = widget.preferredHeight;
            }
        },

        _bindChildProperties: function (panelInfo, properties) {
            var that = this;
            // Check for existence of watch rather than instanceof Stateful since the later check may fail if
            // properties are used across window contexts.
            if (!panelInfo.properties && properties.watch && properties.set && properties.get) {
                panelInfo.properties = properties;
                var watches = [];
                watches.push(properties.watch(LayoutChildProperty.IS_COLLAPSED, function (name, oldValue, newValue) {
                    // Effect collapse change
                    if (newValue !== panelInfo.isCollapsed) {
                        if (that._shouldBlockSuccession && that.selectionManager) {
                            // Block next child selection since the IS_COLLAPSED property is changed due to a keyboard interaction.
                            that.selectionManager.blockSuccession();
                        }
                        if (newValue) {
                            that._collapse(panelInfo);
                        } else {
                            that._restore(panelInfo);
                        }
                    }
                }));
                watches.push(properties.watch(LayoutChildProperty.IS_EXPANDED, function (name, oldValue, newValue) {
                    // Effect collapse change
                    var isExpanded = that._expandedInfo === panelInfo || that._expandingInfo === panelInfo;
                    if (newValue !== isExpanded) {
                        if (newValue) {
                            that._expand(panelInfo);
                        } else {
                            that._restore(panelInfo);
                        }
                    }
                }));
                watches.push(properties.watch(LayoutChildProperty.IS_OPEN, function (name, oldValue, newValue) {
                    if (!newValue) {
                        that._closePanel(panelInfo);
                    }
                }));
                watches.push(properties.watch(LayoutChildProperty.INDEX, function(name, oldValue, newValue) {
                    if (!that._isUpdatingIndices && oldValue !== newValue && oldValue === that._panelInfo.indexOf(panelInfo) && newValue < that._panelInfo.length) {
                        that._changePanelIndex(oldValue, newValue);
                        that.layout();
                    }
                }));
                panelInfo.unbindChildProperties = function () {
                    watches.forEach(function (watch) {
                        watch.remove();
                    });
                    delete panelInfo.properties;
                    delete panelInfo.unbindChildProperties;
                };
            }
        },

        _panelIndexFromDomId: function (id) {
            var i;
            for (i = 0; i < this._panelInfo.length; i++) {
                if (this._panelInfo[i].widget.id === id) {
                    return i;
                }
            }
            return -1;
        },

        _panelIndexFromPanelId: function (panelId) {
            var i;
            for (i = 0; i < this._panelInfo.length; i++) {
                if (this._panelInfo[i].panelId === panelId) {
                    return i;
                }
            }
            return -1;
        },

        _panelIndexFromProperties: function (properties) {
            var i;
            for (i = 0; i < this._panelInfo.length; i++) {
                if (this._panelInfo[i].properties === properties) {
                    return i;
                }
            }
            return -1;
        },

        _panelIndexFromTitle: function (title) {
            var i;
            for (i = 0; i < this._panelInfo.length; i++) {
                if (this._panelInfo[i].properties.title === title) {
                    return i;
                }
            }
            return -1;
        },

        _panelIndexFromString: function (panelIdOrTitle) {
            var index = this._panelIndexFromPanelId(panelIdOrTitle);
            if (index < 0) {
                index = this._panelIndexFromTitle(panelIdOrTitle);
            }
            return index;
        },

        _getPanelInfoFromDomId: function (id) {
            var index = this._panelIndexFromDomId(id);
            return index >= 0 ? this._panelInfo[index] : null;
        },

        _getPanelIndex: function (widgetPanelIdTitleOrIndex) {
            var type = typeof widgetPanelIdTitleOrIndex;
            return type === "number" ? widgetPanelIdTitleOrIndex :
                    (type === "string" ? this._panelIndexFromString(widgetPanelIdTitleOrIndex) : this._panelIndexFromDomId(widgetPanelIdTitleOrIndex.id));
        },

        _collapse: function (panelInfo) {
            if (panelInfo.isCollapsed || !panelInfo.isCollapsible) {
                return;
            }
            panelInfo.explicitlyCollapsed = true;
            panelInfo.explicitlyRestored = false;
            this.layout();
            // The portions aren't updated upon collapse/restore because doing so would make these operations
            // non-reversible in terms of height allocations.  Instead a flag is set to potentially trigger
            // portion updating later.
            this._portionsAreStale = true;
        },

        _expand: function (panelInfo) {
            if (panelInfo === this._expandedInfo || panelInfo === this._expandingInfo || this._panelInfo.length < 2) {
                return;
            }

            var that = this;
            // Remember which panels were collapsed prior to expand
            if (!this._panelsCollapsedPriorToExpand) {
                this._panelsCollapsedPriorToExpand = [];
                this._panelInfo.forEach(function (info) {
                    if (info.explicitlyCollapsed) {
                        that._panelsCollapsedPriorToExpand.push(info);
                    }
                });
            }
            this._panelInfo.forEach(function (info) {
                if (info !== panelInfo && !info.explicitlyCollapsed && info.isCollapsible) {
                    info.explicitlyCollapsed = true;
                }
            });
            panelInfo.explicitlyCollapsed = false;
            panelInfo.explicitlyRestored = true;
            this._expandingInfo = panelInfo;
            this.layout();
        },

        _restore: function (panelInfo) {
            if (panelInfo.isCollapsed) {
                panelInfo.explicitlyCollapsed = false;
                panelInfo.explicitlyRestored = true;
                this._portionsAreStale = true;
            } else if (panelInfo === this._expandedInfo || panelInfo === this._expandingInfo) {
                if (panelInfo === this._expandingInfo) {
                    delete this._expandingInfo;
                }
                this._panelInfo.forEach(function (info) {
                    if (info !== panelInfo) {
                        info.explicitlyCollapsed = false;
                        info.explicitlyRestored = true;
                    }
                });
                if (this._panelsCollapsedPriorToExpand) {
                    this._panelsCollapsedPriorToExpand.forEach(function (info) {
                        info.explicitlyCollapsed = true;
                        info.explicitlyRestored = false;
                    });
                    delete this._panelsCollapsedPriorToExpand;
                }
            } else {
                return;
            }
            this.layout();
        },

        _finishCollapse: function (panelInfo) {
            panelInfo.isCollapsed = true;
            panelInfo.explicitlyRestored = false;
            domStyle.set(panelInfo.widget.domNode, "display", "none");
            if (panelInfo.properties) {
                panelInfo.properties.set(LayoutChildProperty.IS_COLLAPSED, true);
                panelInfo.properties.set(LayoutChildProperty.IS_SHOWING, false);
            }

            if (this.selectionManager) {
                // Unblock succession so that SelectionManager would be able to select next child when a panel is
                // collapsed/expanded using mouse interaction.
                this.selectionManager.unblockSuccession();
            }

            // Reset the flag
            this._shouldBlockSuccession = false;
        },

        _finishRestore: function (panelInfo) {
            panelInfo.isCollapsed = false;
            panelInfo.explicitlyCollapsed = false;
            domStyle.set(panelInfo.widget.domNode, "display", "block");
            if (panelInfo.properties) {
                panelInfo.properties.set(LayoutChildProperty.IS_COLLAPSED, false);
                if (LayoutUtils.isShowing(this.domNode)) {
                    panelInfo.properties.set(LayoutChildProperty.IS_SHOWING, true);
                }
            }
            if (this.selectionManager) {
                // Unblock succession so that SelectionManager would be able to select next child when a panel is
                // collapsed/expanded using mouse interaction.
                this.selectionManager.unblockSuccession();
            }

            // Reset the flag
            this._shouldBlockSuccession = false;
        },

        _toggleCollapse: function (panelInfo, e) {
            if (!panelInfo.titleBar || !panelInfo.titleBar.get(LayoutChildProperty.IS_COLLAPSIBLE)) {
                return;
            }
            if (panelInfo.isCollapsed) {
                this._restore(panelInfo);
                if (panelInfo.properties) {
                    panelInfo.properties.set(LayoutChildProperty.IS_SELECTED, true);
                }
                panelInfo.titleBar.titleButton.setAttribute("aria-expanded", "true");
            } else {
                panelInfo.distributeUnusedHeight = e.ctrlKey;
                this._collapse(panelInfo);
                panelInfo.titleBar.titleButton.setAttribute("aria-expanded", "false");
            }
        },

        _updateCollapseControlVisibility: function () {
            var titleBar, i, panelInfo;
            if (this._panelInfo.length === 1) {
                panelInfo = this._panelInfo[0];
                titleBar = panelInfo.titleBar;
                if (titleBar) {
                    titleBar.set(LayoutChildProperty.IS_COLLAPSIBLE, panelInfo.properties.isCollapsed);
                }
            } else {
                for (i = 0; i < this._panelInfo.length; i++) {
                    panelInfo = this._panelInfo[i];
                    if (panelInfo.titleBar) {
                        panelInfo.titleBar.set(LayoutChildProperty.IS_COLLAPSIBLE, panelInfo.isCollapsible);
                    }
                }
            }
        },

        /**
         * Returns true if the container currently contains the same panel ids as specified in the
         * given state object
         *
         * @private
         * @instance
         */
        _isPopulatedPerState: function (state) {
            if (state.length !== this._panelInfo.length) {
                return false;
            }
            var index;
            for (index = 0; index < state.length; index += 1) {
                var id = state[index].id || state[index].panelId;
                if (this._panelIndexFromPanelId(id ) < 0) {
                    return false;
                }
            }
            return true;
        },

        _restoreState: function (state) {
            // Put the panels in the specified order and set their portions and collapsed states
            var updatedInfo = [];
            updatedInfo.length = this._panelInfo.length;
            var panelInfo;
            var stateIndex, panelIndex;
            for (stateIndex = 0; stateIndex < state.length; stateIndex += 1) {
                panelIndex = this._panelIndexFromPanelId(state[stateIndex].id || state[stateIndex].panelId);
                panelInfo = this._panelInfo[panelIndex];
                panelInfo.portion = state[stateIndex].portion;
                panelInfo.explicitlyCollapsed = state[stateIndex].collapsed;
                if (panelInfo.explicitlyCollapsed !== panelInfo.isCollapsed) {
                    // Finish collapse/restore activity now rather than leaving it to layout since doing
                    // so might alter the portions.
                    if (panelInfo.explicitlyCollapsed) {
                        this._finishCollapse(panelInfo);
                        panelInfo.titleBar.titleButton.setAttribute("aria-expanded", "false");
                    } else {
                        this._finishRestore(panelInfo);
                        panelInfo.titleBar.titleButton.setAttribute("aria-expanded", "true");
                    }
                }
                updatedInfo[stateIndex] = panelInfo;
            }
            this._panelInfo = updatedInfo;
            this.endStateRestore();
            this._updatePanelIndices();
            this.layout();
        },

        _canDragSplitter: function (panelInfo) {
            // Don't permit the top splitter to be dragged
            return this._panelInfo.indexOf(panelInfo) > 0;
        },

        _beginSplitterDrag: function (panelInfo, e) {
            // Initialize starting drag state
            if (mouse.isLeft(e) && this._canDragSplitter(panelInfo)) {
                this._splitterDragInfo = this._initSplitterDragInfo(panelInfo, e.pageX, e.pageY);

                // Attach listeners
                this._splitterDragInfo.connections = [
                    on(this.ownerDocument, touch.move, lang.hitch(this, "_continueSplitterDrag")),
                    on(this.ownerDocument, touch.release, lang.hitch(this, "_endSplitterDrag"))
                ];
                SelectionBlocker.blockUntilRelease(this.domNode);
            }
            LayoutUtils.stopEvent(e);
        },

        _initSplitterDragInfo: function (panelInfo, x, y) {
            var dragInfo = {panelInfo : panelInfo};
            var contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
            var splitterBox = panelInfo.splitterBox || domGeometry.getMarginBox(panelInfo.splitterNode);
            var panelIndex, info;

            dragInfo.startY = y;
            dragInfo.initialPosition  = splitterBox.t;
            dragInfo.splitterPosition = splitterBox.t;

            // Record the starting heights and collapsed states of each panel
            dragInfo.initialHeights = [];
            dragInfo.initialHeights.length = this._panelInfo.length;
            for (panelIndex = 0; panelIndex < this._panelInfo.length; panelIndex += 1) {
                info = this._panelInfo[panelIndex];
                dragInfo.initialHeights[panelIndex] = info.isCollapsed ?
                        (this._allowExpand(info, panelInfo) ? this._getMinPanelHeight(info) : 0) :
                        domGeometry.getMarginBox(info.widget.domNode).h;
            }
            dragInfo.initialCollapsedStates = this._getCollapsedStates();

            // Compute drag limits
            panelIndex = this._panelInfo.indexOf(panelInfo);
            dragInfo.lowerLimit = contentBox.t + this._getMinHeight(0, panelIndex);
            dragInfo.upperLimit = contentBox.t + contentBox.h - this._getMinHeight(panelIndex, this._panelInfo.length - panelIndex);

            return dragInfo;
        },

        _continueSplitterDrag: function (e) {
            var dragInfo = this._splitterDragInfo;
            var offset   = e.pageY - dragInfo.startY;
            var newPosition = dragInfo.initialPosition + offset;
            dragInfo.proportionalResize = e.ctrlKey;
            if (newPosition < dragInfo.lowerLimit) {
                newPosition = dragInfo.lowerLimit;
            } else if (newPosition > dragInfo.upperLimit) {
                newPosition = dragInfo.upperLimit;
            }
            if (newPosition !== dragInfo.splitterPosition) {
                dragInfo.hasMoved = true;
                dragInfo.splitterPosition = newPosition;
                this.layout();
            }
        },

        _endSplitterDrag: function () {
            // Update portions
            var dragInfo = this._splitterDragInfo;
            if (dragInfo.hasMoved) {
                if (!this._animationTimer) {
                    // Update the portions to reflect the new splitter position, unless a panel was
                    // collapsed by the drag.
                    var collapsedStates = this._getCollapsedStates();
                    if (collapsedStates.every(function(value, index) {
                            return !value || dragInfo.initialCollapsedStates[index];
                        })) {
                        this._updatePortions();
                    }
                    delete this._splitterDragInfo;
                } else {
                    this._animationDoneResponse = function () {
                        this._updatePortions();
                        delete this._splitterDragInfo;
                    };
                }
                this.emit(LayoutContainerEvent.STATE_CHANGED);
            } else {
                delete this._splitterDragInfo;
            }
            // Remove drag listeners
            dragInfo.connections.forEach(function (connection) {
                connection.remove();
            });
        },

        _beginTitleDrag: function (panelInfo, e) {
            if (mouse.isLeft(e)) {
                this._beginTitleDragAtPoint(panelInfo, e.pageX, e.pageY);
            } else if (panelInfo.closable && mouse.isMiddle(e) && !has("ie")) {
                this._closePanel(panelInfo);
            }
        },

        _beginTitleDragAtPoint: function (panelInfo, x, y) {
            // Initialize starting drag state
            this._titleDragInfo = this._initTitleDragInfo(panelInfo, x, y);

            // Attach listeners
            this._titleDragInfo.connections = [
                on(this.ownerDocument, touch.move, lang.hitch(this, "_continueTitleDrag")),
                on(this.ownerDocument, touch.release, lang.hitch(this, "_endTitleDrag", false))
            ];
            SelectionBlocker.blockUntilRelease(this.domNode);
        },

        _initTitleDragInfo: function (panelInfo, x, y) {
            var dragInfo = {panelInfo : panelInfo};
            dragInfo.startY = y;
            dragInfo.startX = x;
            dragInfo.lastY  = y;
            dragInfo.initialIndex = this._panelInfo.indexOf(panelInfo);
            dragInfo.currentIndex = dragInfo.initialIndex;
            dragInfo.panelHeight = domGeometry.getMarginBox(panelInfo.widget.domNode).h;
            dragInfo.initialTops = this._getTops();
            return dragInfo;
        },

        /**
         * Get the location of the "tops" of the title bar or visual splitter above each panel
         *
         * @private
         * @instance
         */
        _getTops: function () {
            var info, lastBox, index;
            var initialTops = [];
            initialTops.length = this._panelInfo.length + 1;
            for (index = 0; index < this._panelInfo.length; index += 1) {
                info = this._panelInfo[index];
                initialTops[index] = info.titleBar ?
                    domGeometry.getMarginBox(info.titleBar.domNode).t :
                    domGeometry.getMarginBox(info.splitterNode).t + this._splitterOverlap;
            }
            // Compute an extra "top" representing the bottom of the last panel
            if (!info) {
                lastBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
                initialTops[index] = lastBox.t + lastBox.h;
            } else if (info.isCollapsed) {
                // If the last panel is collapsed then the bottom is computed by simply adding the title bar or visible splitter height
                initialTops[index] = initialTops[index - 1] + (info.titleBar ? TitleBar.getPreferredHeight() : this._visibleSplitterThickness);
            } else {
                // Get the bottom of the final panel
                lastBox = domGeometry.getMarginBox(info.widget.domNode);
                initialTops[index] = lastBox.t + lastBox.h;
            }
            return initialTops;
        },

        _getCollapsedStates: function() {
            var collapsedStates = [];
            collapsedStates.length = this._panelInfo.length;
            for (var index = 0; index < this._panelInfo.length; index++) {
                collapsedStates[index] = this._panelInfo[index].isCollapsed;
            }
            return collapsedStates;
        },

        _continueTitleDrag: function (e) {
            var dragInfo = this._titleDragInfo;
            var panelInfo = dragInfo.panelInfo;
            var offset = e.pageY - dragInfo.startY;
            var direction = e.pageY - dragInfo.lastY;
            var containerBox = domGeometry.position(this.domNode);
            var contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
            var height, top, bottom, center, containerBottom, childBox, belowInitial, aboveInitial, neighborIndex, neighborInfo;
            if (!dragInfo.hasMoved) {
                if (dragInfo.startY === e.pageY && dragInfo.startX === e.pageX) {
                    return;  // Hasn't moved yet
                }
                // When the user actually starts dragging move the panel being dragged and its title bar to
                // the top of the z-order.  Also place a drag background behind the panel (in case it's not opaque).
                domClass.add(panelInfo.titleBar.domNode, "movingPanel");
                if (!panelInfo.isCollapsed) {
                    domClass.add(panelInfo.widget.domNode, "movingPanel");
                }
                domStyle.set(panelInfo.splitterNode, {visibility: "hidden"});
                panelInfo.moveBackground = domConstruct.create("div", {id : this.id + "MoveBackground"}, this.domNode);
                domClass.add(panelInfo.moveBackground, "moveBackground");
                dragInfo.hasMoved = true;
            }
            dragInfo.lastY = e.pageY;

            // See if the panel has been dragged out of the container
            if (this.dragOutEnabled && (e.pageX < containerBox.x || e.pageX > (containerBox.x + containerBox.w))) {
                this._dragOut(panelInfo, {x: e.pageX, y: e.pageY});
                return;
            }

            // Reposition the panel being moved (if not collapsed) and its title bar (subject to container bounds)
            childBox = lang.clone(contentBox);
            childBox.t = dragInfo.initialTops[dragInfo.initialIndex];
            containerBottom = contentBox.t + contentBox.h;
            height = dragInfo.initialTops[dragInfo.initialIndex + 1] - childBox.t;
            childBox.t += offset;
            // Restrict panel to stay within container
            if (childBox.t < contentBox.t) {
                childBox.t = contentBox.t;
            } else if (childBox.t + height > containerBottom) {
                childBox.t = containerBottom - height;
            }
            top = childBox.t;
            bottom = childBox.t + height;
            childBox.h = height;
            this._positionPanel(panelInfo, childBox);

            // Update the panel order
            if (direction < 0 && dragInfo.currentIndex > 0) {
                // See if the dragged panel's top edge has moved above its neighbor's center
                belowInitial = dragInfo.currentIndex > dragInfo.initialIndex;
                neighborIndex = belowInitial ? dragInfo.currentIndex : (dragInfo.currentIndex - 1);
                center = (dragInfo.initialTops[neighborIndex] + dragInfo.initialTops[neighborIndex + 1]) / 2 -
                         (belowInitial ? height : 0);
                if (top < center) {
                    // Move the preceding panel (and its title or splitter) downward
                    neighborInfo = this._panelInfo[neighborIndex];
                    childBox.t = dragInfo.initialTops[neighborIndex];
                    childBox.h = dragInfo.initialTops[neighborIndex + 1] - childBox.t;
                    if (neighborIndex < dragInfo.initialIndex) {
                        childBox.t += height;
                    }
                    this._movePanel(neighborInfo, childBox);
                    dragInfo.currentIndex -= 1;
                }
            } else if (direction > 0 && dragInfo.currentIndex < (this._panelInfo.length - 1)) {
                // See if the dragged panel's bottom edge has moved below its neighbor's center
                aboveInitial = dragInfo.currentIndex < dragInfo.initialIndex;
                neighborIndex = aboveInitial ? dragInfo.currentIndex : (dragInfo.currentIndex + 1);
                center = (dragInfo.initialTops[neighborIndex] + dragInfo.initialTops[neighborIndex + 1]) / 2 +
                         (aboveInitial ? height : 0);
                if (bottom > center) {
                    // Move the subsequent panel (and its title or splitter) upward
                    neighborInfo = this._panelInfo[neighborIndex];
                    childBox.t = dragInfo.initialTops[neighborIndex];
                    childBox.h = dragInfo.initialTops[neighborIndex + 1] - childBox.t;
                    if (neighborIndex > dragInfo.initialIndex) {
                        childBox.t -= height;
                    }
                    this._movePanel(neighborInfo, childBox);
                    dragInfo.currentIndex += 1;
                }
            }
        },

        _endTitleDrag: function (wasRemoved) {
            var that = this;
            var dragInfo = this._titleDragInfo;
            var panelInfo = dragInfo.panelInfo;
            var moved = dragInfo.hasMoved;
            var reordered;
            var finalBox;
            if (wasRemoved) {
                // Cancel any in-progress animation
                if (this._animationTimer) {
                    clearInterval(this._animationTimer);
                    delete this._animationTimer;
                    this._panelInfo.forEach(function (info){
                        delete info.animationStartTime;
                        delete info.animationDuration;
                        delete info.animationStartHeight;
                        delete info.animationHeightChange;
                    });
                }
                // Remove drag background
                if (panelInfo.moveBackground) {
                    domConstruct.destroy(panelInfo.moveBackground);
                    delete panelInfo.moveBackground;
                }
            } else if (moved) {
                // Update the panel array if necessary
                reordered = dragInfo.currentIndex !== dragInfo.initialIndex;
                if (reordered) {
                    this._changePanelIndex(dragInfo.initialIndex, dragInfo.currentIndex);
                }

                // Animate dragged panel to its final location
                finalBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
                finalBox.t = dragInfo.currentIndex <= dragInfo.initialIndex ? dragInfo.initialTops[dragInfo.currentIndex]
                    : (dragInfo.initialTops[dragInfo.currentIndex + 1] - dragInfo.panelHeight);
                finalBox.h = dragInfo.panelHeight;
                this._movePanel(panelInfo, finalBox);
                this._animationDoneResponse = function () {
                    // Restore z-order
                    domClass.remove(panelInfo.titleBar.domNode, "movingPanel");
                    if (!panelInfo.isCollapsed) {
                        domClass.remove(panelInfo.widget.domNode, "movingPanel");
                    }
                    domStyle.set(panelInfo.splitterNode, {visibility: "visible"});
                    // Remove drag background
                    if (panelInfo.moveBackground) {
                        domConstruct.destroy(panelInfo.moveBackground);
                        delete panelInfo.moveBackground;
                    }

                    if (moved) {
                        that.layout();
                        if (reordered) {
                            this.emit(LayoutContainerEvent.STATE_CHANGED);
                        }
                    }
                };
            }

            // Remove drag listeners
            dragInfo.connections.forEach(function (connection) {
                connection.remove();
            });
            delete this._titleDragInfo;

            if (!this._animationTimer && this._animationDoneResponse) {
                this._animationDoneResponse();
                delete this._animationDoneResponse;
            }
        },

        _changePanelIndex: function (fromIndex, toIndex) {
            var panelInfo = this._panelInfo[fromIndex];
            this._panelInfo.splice(fromIndex, 1);
            this._panelInfo.splice(toIndex, 0, panelInfo);
            this._updatePanelIndices();
        },

        _dragOut: function (panelInfo, mousePoint) {
            var dragInfo = this._titleDragInfo;
            var box;
            if (panelInfo.isCollapsed) {
                box = domGeometry.position(panelInfo.titleBar.domNode);
                box.h = 200;
            } else {
                box = domGeometry.position(panelInfo.widget.domNode);
            }
            box.l = box.x;
            box.t = box.y;

            var dragOutInfo = {
                widget: panelInfo.widget,
                properties: panelInfo.properties,
                index: dragInfo.initialIndex,
                portion: panelInfo.portion,
                isCollapsed: panelInfo.isCollapsed,
                box: box,
                mousePoint: mousePoint
            };
            domClass.remove(panelInfo.widget.domNode, "movingPanel");
            this.emit(LayoutContainerEvent.DRAG_OUT, dragOutInfo);

            // If the panel wasn't removed in response to the drag out event, end the drag
            // (if it was removed the drag will have been ended by the remove).
            if (this._panelInfo.indexOf(panelInfo) >= 0) {
                this._endTitleDrag(false);
            }
        },

        /**
         * Adds a child and begins a drag operation on its title bar.
         *
         * @param dragOutInfo {Object} information captured when the child was dragged out of its previous parent,
         *                             including widget and properties.
         * @param mousePoint {Object} contains the x and y mouse coordinates
         * @instance
         */
        dragIn: function (dragOutInfo, mousePoint) {
            var index, tops;
            if (mousePoint) {
                // Find the index for the panel by comparing the mouse position to the tops
                // of existing panels.
                tops = this._getTops();
                for (index = 0; index < tops.length - 1; index += 1) {
                    if (mousePoint.y < (tops[index] + tops[index + 1]) / 2) {
                        break;
                    }
                }
            } else {
                index = dragOutInfo.index;
            }
            this.addWithProperties(dragOutInfo.widget, dragOutInfo.properties, index);

            // Initialize the drag state so that continued dragging can reposition the panel or drag it back out.
            if (mousePoint) {
                this._beginTitleDragAtPoint(this._getPanelInfoFromDomId(dragOutInfo.widget.id), mousePoint.x, mousePoint.y);
            }
        },

        _positionPanel: function (panelInfo, box) {
            var panelHeight = box.h;
            var splitBox, backgroundBox;
            // If this panel is being moved, position the move background behind it.
            if (panelInfo.moveBackground) {
                backgroundBox = lang.clone(box);
                backgroundBox.t -= 1;  // Space for top border
                backgroundBox.h += 2;  // Also space for bottom border
                domGeometry.setMarginBox(panelInfo.moveBackground, backgroundBox);
            }
            if (panelInfo.titleBar) {
                box.h = TitleBar.getPreferredHeight();
                domGeometry.setMarginBox(panelInfo.titleBar.domNode, box);
                box.t += box.h;
                panelHeight -= box.h;
            } else {
                splitBox = lang.clone(box);
                splitBox.t -= this._splitterOverlap;
                splitBox.h = this._splitterThickness;
                domGeometry.setMarginBox(panelInfo.splitterNode, splitBox);
                box.t += this._visibleSplitterThickness;
                panelHeight -= this._visibleSplitterThickness;
            }
            if (!panelInfo.isCollapsed) {
                box.h = panelHeight;
                LayoutUtils.setSize(panelInfo.widget, box);
            }
        },

        _movePanel: function (panelInfo, toBox) {
            if (!(this.animationEnabled && LayoutUtils.isAnimationEnabled())) {
                this._positionPanel(panelInfo, toBox);
            } else {
                panelInfo.animationStartTop = panelInfo.titleBar ? domGeometry.getMarginBox(panelInfo.titleBar.domNode).t :
                                                                   domGeometry.getMarginBox(panelInfo.splitterNode).t + this._splitterOverlap;
                panelInfo.animationStopBox  = toBox;
                panelInfo.animationStartTime = new Date().valueOf();
                panelInfo.animationDuration  = AnimationTiming.computeDuration(toBox.t - panelInfo.animationStartTop);
                if (!this._animationTimer) {
                    this._animationTimer = setInterval(lang.hitch(this, "_updatePanelPositions"), AnimationTiming.UPDATE_INTERVAL);
                }
            }
        },

        _closePanel: function (panelInfo) {
            var saveProperties = panelInfo.properties;
            this.removeChild(panelInfo.widget);
            if (saveProperties) {
                saveProperties.set(LayoutChildProperty.IS_OPEN, false);
                saveProperties.set(LayoutChildProperty.IS_SHOWING, false);
            }
        },

        _updatePanelPositions: function () {
            var continueAnimating = false;
            var currentTime = new Date().valueOf();
            var i, panelInfo, box, elapsedTime;
            for (i = 0; i < this._panelInfo.length; i++) {
                panelInfo = this._panelInfo[i];
                if (panelInfo.animationStartTime && panelInfo.animationStopBox) {
                    elapsedTime = currentTime - panelInfo.animationStartTime;
                    box = lang.clone(panelInfo.animationStopBox);
                    if (elapsedTime < panelInfo.animationDuration) {
                        box.t = panelInfo.animationStartTop + (box.t - panelInfo.animationStartTop) *
                                easing.cubicInOut(elapsedTime / panelInfo.animationDuration);
                        continueAnimating = true;
                    } else {
                        delete panelInfo.animationStartTime;
                        delete panelInfo.animationDuration;
                    }
                    this._positionPanel(panelInfo, box);
                }
            }
            if (!continueAnimating) {
                clearInterval(this._animationTimer);
                delete this._animationTimer;
                if (this._animationDoneResponse) {
                    this._animationDoneResponse();
                    delete this._animationDoneResponse;
                }
            }
        },

        _updatePortions: function (availHeight) {
            var panelInfo, panelHeight, i;
            availHeight = availHeight || domGeometry.getContentBox(this.domNode, this.computedStyle).h - this._getOverhead();
            for (i = 0; i < this._panelInfo.length; i++) {
                panelInfo = this._panelInfo[i];
                if (!panelInfo.isCollapsed) {
                    panelHeight = domGeometry.getContentBox(panelInfo.widget.domNode).h;
                } else {
                    panelHeight = this._getMinPanelHeight(panelInfo);
                }
                panelInfo.portion = panelHeight / availHeight;
            }
            this._portionsAreStale = false;
        },

        /**
         * Computes cumulative height of all title bars and splitters
         *
         * @private
         * @instance
         */
        _getOverhead: function (startIndex, endIndex) {
            if (startIndex === undefined) {
                startIndex = 0;
                endIndex = this._panelInfo.length - 1;
            } else if (startIndex > endIndex) {
                var temp = startIndex;
                startIndex = endIndex;
                endIndex = temp;
            }
            var overhead = 0;
            var i, panelInfo;
            for (i = startIndex; i <= endIndex; i++) {
                panelInfo = this._panelInfo[i];
                if (panelInfo.titleBar) {
                    overhead += TitleBar.getPreferredHeight();
                } else if (i > 0) {
                    overhead += this._visibleSplitterThickness;
                }
            }
            return overhead;
        },

        _getMinPanelHeight: function (panelInfo) {
            return !panelInfo.isResizable ? panelInfo.initialHeight : (panelInfo.minSize instanceof Function ? panelInfo.minSize() : panelInfo.minSize);
        },

        _getMaxPanelHeight: function (panelInfo) {
            return !panelInfo.isResizable ? panelInfo.initialHeight : (panelInfo.maxSize instanceof Function ? panelInfo.maxSize() : panelInfo.maxSize);
        },

        /**
         * Computes the minimum height of the given range of panels.  The returned height includes title bar heights,
         * visible splitter heights and minimum heights of non-collapsible panels
         *
         * @private
         * @instance
         */
        _getMinHeight: function (firstPanelIndex, panelCount) {
            var i, panelInfo;
            var minHeight = 0;
            for (i = firstPanelIndex; i < firstPanelIndex + panelCount; i++) {
                panelInfo = this._panelInfo[i];
                if (panelInfo.titleBar) {
                    minHeight += TitleBar.getPreferredHeight();
                } else if (i > 0) {
                    minHeight += this._visibleSplitterThickness;
                }
                if (!this._canCollapse(panelInfo)) {
                    minHeight += this._getMinPanelHeight(panelInfo);
                }
            }
            return minHeight;
        },

        _canCollapse: function (panelInfo) {
            return (this.autoCollapseEnabled && panelInfo.isCollapsible) || panelInfo.isCollapsed;
        },

        /**
         * Returns an array containing the height that should be allocated to each panel.
         * Zero height means that a panel should be collapsed.
         *
         * @private
         * @instance
         */
        _calculateAllHeights: function (autoRestoreProhibited) {
            var contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
            var heights    = [];
            heights.length = this._panelInfo.length;
            var dragInfo, dragIndex, heightAbove;
            if (this._splitterDragInfo) {
                // Allocate height to panels above and below splitter being dragged
                dragInfo = this._splitterDragInfo;
                dragIndex = this._panelInfo.indexOf(dragInfo.panelInfo);
                heightAbove = dragInfo.splitterPosition - contentBox.t;
                heightAbove = this._calculateHeights(dragIndex - 1, 0, heightAbove, dragInfo.proportionalResize, false, heights);
                this._calculateHeights(dragIndex, this._panelInfo.length - 1, contentBox.h - heightAbove, dragInfo.proportionalResize, false, heights);
            } else {
                this._calculateHeights(this._panelInfo.length - 1, 0, contentBox.h, true, autoRestoreProhibited, heights);
            }
            return heights;
        },

        /**
         * Given the available height and a subset of the child panels, fills entries in an array containing
         * the heights that should be allocated to the panels.  Zero height means that a panel should be collapsed.
         *
         * @private
         * @instance
         */
        _calculateHeights: function (minPriorityIndex, maxPriorityIndex, availHeight, proportional, autoRestoreProhibited, heights) {
            var usedHeight = availHeight;
            var fixedHeightTotal = 0;
            availHeight -= this._getOverhead(minPriorityIndex, maxPriorityIndex);
            // Create a priority-ordered indexing of the panels
            var panelIndices = [];
            var minIndex, maxIndex;
            var i, panelInfo, panelIndex, fixedHeight, totalHeight, nonFixedPortion, defaultPortion, portionToReallocate, surplus, addedHeight, iterationCount;
            var deficit = 0;
            var minHeights = [];
            var maxHeights = [];
            minHeights.length = heights.length;
            maxHeights.length = heights.length;
            if (minPriorityIndex <= maxPriorityIndex) {
                minIndex = minPriorityIndex;
                maxIndex = maxPriorityIndex;
                for (i = minPriorityIndex; i <= maxPriorityIndex; i++) {
                    panelIndices.push(i);
                }
            } else {
                minIndex = maxPriorityIndex;
                maxIndex = minPriorityIndex;
                for (i = minPriorityIndex; i >= maxPriorityIndex; i--) {
                    panelIndices.push(i);
                }
            }

            if (proportional) {
                // Size panels according to their portions
                // Calculate the portion normalization factor
                nonFixedPortion = 0;
                portionToReallocate = 0;
                defaultPortion = (1.0 / this._panelInfo.length);
                for (i = minIndex; i <= maxIndex; i++) {
                    panelInfo = this._panelInfo[i];
                    if (!this._keepCollapsed(panelInfo, autoRestoreProhibited)) {
                        fixedHeight = this._getFixedHeight(panelInfo);
                        if (fixedHeight) {
                            fixedHeightTotal += fixedHeight;
                            panelInfo.workingPortion = 0;
                        } else {
                            panelInfo.workingPortion = panelInfo.portion || defaultPortion;
                            if (portionToReallocate > 0) {
                                panelInfo.workingPortion += portionToReallocate;
                                portionToReallocate = 0;
                            }
                            nonFixedPortion += panelInfo.workingPortion;
                        }
                    } else if (!panelInfo.isCollapsed && !panelInfo.distributeUnusedHeight) {
                        portionToReallocate += panelInfo.portion || defaultPortion;
                    }
                }
                // If there is a left over portion from a collapsed panel allocate it to an earlier panel
                if (portionToReallocate > 0) {
                    for (i = maxIndex - 1; i >= minIndex; i--) {
                        panelInfo = this._panelInfo[i];
                        if (!this._keepCollapsed(panelInfo, autoRestoreProhibited)) {
                            fixedHeight = this._getFixedHeight(panelInfo);
                            if (fixedHeight) {
                                fixedHeightTotal += fixedHeight;
                                panelInfo.workingPortion = 0;
                            } else {
                                panelInfo.workingPortion += portionToReallocate;
                                nonFixedPortion += portionToReallocate;
                                break;
                            }
                        }
                    }
                }

                // Compute proportional heights, enforcing minimums and maximums
                totalHeight = 0;
                for (i = 0; i < panelIndices.length; i++) {
                    panelIndex = panelIndices[i];
                    panelInfo = this._panelInfo[panelIndex];
                    heights[panelIndex] = 0;
                    if (!this._keepCollapsed(panelInfo, autoRestoreProhibited)) {
                        minHeights[panelIndex] = this._getMinPanelHeight(panelInfo);
                        maxHeights[panelIndex] = this._getMaxPanelHeight(panelInfo);
                        heights[panelIndex] =  this._getFixedHeight(panelInfo);
                        if (!heights[panelIndex]) {
                            heights[panelIndex] = Math.min(Math.max((availHeight - fixedHeightTotal) * panelInfo.workingPortion / nonFixedPortion,
                                                           minHeights[panelIndex]), maxHeights[panelIndex]);
                        }
                        totalHeight += heights[panelIndex];
                    }
                }
                // Reduce heights and collapse panels as necessary
                deficit = totalHeight - availHeight;
                if (deficit > 0) {
                    deficit = this._reduceThenCollapse(deficit, panelIndices, minHeights, heights);
                }

                // Proportionally redistribute any space left over after collapsing
                // Make multiple passes if necessitated by application of max height limit.
                for (iterationCount = 0; deficit < 0 && iterationCount < 3; iterationCount++) {
                    // Compute normalization factor for remaining non-collapsed
                    nonFixedPortion = 0;
                    for (i = 0; i < panelIndices.length; i++) {
                        panelIndex = panelIndices[i];
                        panelInfo = this._panelInfo[panelIndex];
                        if (heights[panelIndex] > 0 && heights[panelIndex] < maxHeights[panelIndex]) {
                            nonFixedPortion += panelInfo.workingPortion;
                        }
                    }
                    if (nonFixedPortion === 0) {
                        break;
                    }

                    surplus = -deficit;
                    for (i = 0; i < panelIndices.length; i++) {
                        panelIndex = panelIndices[i];
                        panelInfo = this._panelInfo[panelIndex];
                        if (heights[panelIndex] > 0) {
                            addedHeight = surplus * panelInfo.workingPortion / nonFixedPortion;
                            deficit += addedHeight;
                            heights[panelIndex] += addedHeight;
                            if (heights[panelIndex] > maxHeights[panelIndex]) {
                                deficit += maxHeights[panelIndex] - heights[panelIndex];
                                heights[panelIndex] = maxHeights[panelIndex];
                            }
                        }
                    }
                }
            } else if (this._splitterDragInfo) {
                // Set the heights to their initial (from start of drag) values
                totalHeight = 0;
                for (i = 0; i < panelIndices.length; i++) {
                    panelIndex = panelIndices[i];
                    panelInfo = this._panelInfo[panelIndex];
                    minHeights[panelIndex] = this._getMinPanelHeight(panelInfo);
                    maxHeights[panelIndex] = this._getMaxPanelHeight(panelInfo);
                    heights[panelIndex] = this._splitterDragInfo.initialHeights[panelIndex];
                    if (heights[panelIndex] === 0 && !panelInfo.isCollapsed) {
                        heights[panelIndex] = minHeights[panelIndex];
                    }
                    totalHeight += heights[panelIndex];
                }

                // Reduce heights and collapse panels as necessary
                deficit = totalHeight - availHeight;
                if (deficit > 0) {
                    deficit = this._reduceAndCollapse(deficit, panelIndices, minHeights, heights);
                }
                // Give any surplus to the first non-collapsed panel
                if (deficit < 0) {
                    for (i = 0; i < panelIndices.length; i++) {
                        panelIndex = panelIndices[i];
                        if (heights[panelIndex] > 0) {
                            heights[panelIndex] -= deficit;
                            deficit = 0;
                            if (heights[panelIndex] > maxHeights[panelIndex]) {
                                deficit = maxHeights[panelIndex] - heights[panelIndex];
                                heights[panelIndex] = maxHeights[panelIndex];
                            } else {
                                break;
                            }
                        }
                    }
                }

                // If a non-collapsed panel was not found give the surplus to a non-collapsed
                // panel above the range being considered here.
                if (deficit < 0 && maxIndex === (this._panelInfo.length - 1)) {
                    for (panelIndex = minIndex - 1; panelIndex >= 0; panelIndex -= 1) {
                        if (heights[panelIndex] > 0) {
                            heights[panelIndex] -= deficit;
                            deficit = 0;
                            if (heights[panelIndex] > maxHeights[panelIndex]) {
                                deficit = maxHeights[panelIndex] - heights[panelIndex];
                                heights[panelIndex] = maxHeights[panelIndex];
                            } else {
                                break;
                            }
                        }
                    }
                }
            }
            usedHeight += deficit;
            return usedHeight;
        },

        /**
         * This helper reduces the combined panel heights by a specified amount.
         * The reduction is achieved by first adjusting panels heights downward as needed.
         * If further reduction is needed after all panels have been reduced to their
         * minimum heights, panels are then collapsed as needed.
         * Returns the remaining deficit, which may be negative indicating a surplus.
         *
         * @private
         * @instance
         */
        _reduceThenCollapse: function (deficit, panelIndices, minHeights, heights) {
            var panelIndex, panelInfo, i, reduction;

            // First try to make up any deficit by reducing panels to their minimum size
            for (i = 0; deficit > 0 && i < panelIndices.length; i++) {
                panelIndex = panelIndices[i];
                panelInfo = this._panelInfo[panelIndex];
                if (heights[panelIndex] > 0 && heights[panelIndex] > minHeights[panelIndex]) {
                    reduction = Math.min(heights[panelIndex] - minHeights[panelIndex], deficit);
                    heights[panelIndex] -= reduction;
                    deficit -= reduction;
                }
            }
            // If there is still a deficit, collapse panels as necessary.
            // Make two passes, the first favoring panels that are already collapsed (or collapsing?)
            if (deficit > 0 && this.autoCollapseEnabled) {
                for (i = 0; deficit > 0 && i < panelIndices.length; i++) {
                    panelIndex = panelIndices[i];
                    panelInfo = this._panelInfo[panelIndex];
                    if (heights[panelIndex] > 0 && panelInfo.isCollapsed) {
                        if (deficit < COLLAPSE_THRESHOLD) {
                            heights[panelIndex] -= deficit;
                            deficit = 0;
                        } else if (panelInfo.isCollapsible) {
                            deficit -= heights[panelIndex];
                            heights[panelIndex] = 0;
                        }
                    }
                }
                for (i = 0; deficit > 0 && i < panelIndices.length; i++) {
                    panelIndex = panelIndices[i];
                    panelInfo = this._panelInfo[panelIndex];
                    if (heights[panelIndex] > 0) {
                        if (deficit < COLLAPSE_THRESHOLD) {
                            heights[panelIndex] -= deficit;
                            deficit = 0;
                        } else if (panelInfo.isCollapsible) {
                            deficit -= heights[panelIndex];
                            heights[panelIndex] = 0;
                        }
                    }
                }
            }
            return deficit;
        },

        /**
         * This helper reduces the combined panel heights by a specified amount.
         * The reduction is achieved by reducing the panels in succession, where reduction
         * consists of height adjustment or collapse.
         * Returns the remaining deficit, which may be negative indicating a surplus.
         *
         * @private
         * @instance
         */
        _reduceAndCollapse: function (deficit, panelIndices, minHeights, heights) {
            var panelIndex, panelInfo, i, excess, reduction;

            for (i = 0; deficit > 0 && i < panelIndices.length; i++) {
                panelIndex = panelIndices[i];
                panelInfo = this._panelInfo[panelIndex];
                if (heights[panelIndex] > 0) {
                    excess = heights[panelIndex] - minHeights[panelIndex];
                    if (excess < deficit && this.autoCollapseEnabled && panelInfo.isCollapsible) {
                        deficit -= heights[panelIndex];
                        heights[panelIndex] = 0;
                    } else if (excess > 0) {
                        reduction = Math.min(excess, deficit);
                        heights[panelIndex] -= reduction;
                        deficit -= reduction;
                    }
                }
            }
            return deficit;
        },

        _isCollapsing: function (panelInfo) {
            return panelInfo.animationStartHeight !== undefined && (panelInfo.animationStartHeight + panelInfo.animationHeightChange) === 0;
        },

        _isRestoring: function (panelInfo) {
            return panelInfo.animationStartHeight === 0;
        },

        _keepCollapsed: function (panelInfo, autoRestoreProhibited) {
            return panelInfo.explicitlyCollapsed || (panelInfo.isCollapsed && !panelInfo.explicitlyRestored && autoRestoreProhibited);
        },

        _allowExpand: function(panelInfo, dragInfo) {
            if (!panelInfo.explicitlyCollapsed) {
                return true;
            }
            var panelIndex = this._panelInfo.indexOf(panelInfo);
            var dragIndex = this._panelInfo.indexOf(dragInfo);
            var anyExpandable, index;
            if (panelIndex === dragIndex - 1) {
                anyExpandable = false;
                for (index = panelIndex - 1; index >= 0 && !anyExpandable; index--) {
                    var info = this._panelInfo[index];
                    anyExpandable = !(info.isCollapsed && info.explicitlyCollapsed);
                }
            } else if (panelIndex === dragIndex) {
                anyExpandable = false;
                for (index = panelIndex + 1; index < this._panelInfo.length && !anyExpandable; index++) {
                    var info = this._panelInfo[index];
                    anyExpandable = !(info.isCollapsed && info.explicitlyCollapsed);
                }
            }
            if (anyExpandable === false) {
                return true;
            }
            return false;
        },

        _getFixedHeight: function (panelInfo) {
            return panelInfo.userAdjustedHeight && panelInfo.isResizable ? undefined : panelInfo.initialHeight;
        },

        _updatePanelIndices: function () {
            this._isUpdatingIndices = true;
            var panelInfo, properties;
            for (i = 0; i < this._panelInfo.length; i++) {
                panelInfo = this._panelInfo[i];
                properties = panelInfo.properties;
                if (properties && properties.get(LayoutChildProperty.INDEX) !== i) {
                    properties.set(LayoutChildProperty.INDEX, i);
                }
                if (panelInfo.splitterNode) {
                    domStyle.set(panelInfo.splitterNode, "display", i === 0 ? "none" : "block");
                }
            }
            this._isUpdatingIndices = false;
        },

        _handleNavigation: function (direction) {
            var navigationTargets = [];
            var actionNodeWrappers = [];

            // Add panels' title bars
            this._panelInfo.forEach(function (panel) {
                if (panel.titleBar && panel.titleBar.titleButton && panel.titleBar.actionsNodeWrapper) {
                    navigationTargets.push(panel.titleBar.titleButton);
                    actionNodeWrappers.push(panel.titleBar.actionsNodeWrapper);
                }
            });

            this._extraNavigationTargets.forEach(function (target) {
                navigationTargets.push(target);
            });

            // Determine the index of the currently focused element
            var focusedNode = document.activeElement;
            var focusedNodeIndex = navigationTargets.indexOf(focusedNode);

            if (focusedNodeIndex === -1) {
                // focus is in the actions button
                focusedNodeIndex = actionNodeWrappers.indexOf(focusedNode);
            }

            if (focusedNodeIndex >= 0) {
                switch (direction) {
                    case "up":
                        // Get the focus on the last tab
                        if (focusedNodeIndex === 0) {
                            navigationTargets[navigationTargets.length - 1].focus();
                            if (this.selectionManager[SelectionManager.SELECTED_CHILD]) {
                                this.selectionManager.setSelected(this.selectionManager[SelectionManager.SELECTED_CHILD], false, true);
                            }
                        }
                        // Get the focus on the previous tab
                        else if (focusedNodeIndex > 0) {
                            this.selectionManager.blockRevealChild();
                            this.selectionManager.setSelected(this._panelInfo[focusedNodeIndex - 1].widget, true, true);
                            this.selectionManager.unblockRevealChild();
                            navigationTargets[focusedNodeIndex - 1].focus();
                        }
                        break;

                    case "down":
                        // Get the focus on the first tab
                        if (focusedNodeIndex === navigationTargets.length - 1) {
                            this.selectionManager.blockRevealChild();
                            this.selectionManager.setSelected(this._panelInfo[0].widget, true, true);
                            this.selectionManager.unblockRevealChild();
                            navigationTargets[0].focus();
                        }

                        // Get the focus on the next tab
                        else if (focusedNodeIndex >= 0) {
                            if (focusedNodeIndex + 1 !== navigationTargets.length - 1) {
                                this.selectionManager.blockRevealChild();
                                this.selectionManager.setSelected(this._panelInfo[focusedNodeIndex + 1].widget, true, true);
                                this.selectionManager.unblockRevealChild();
                            } else {
                                if (this.selectionManager[SelectionManager.SELECTED_CHILD]) {
                                    this.selectionManager.setSelected(this.selectionManager[SelectionManager.SELECTED_CHILD], false, true);
                                }
                            }
                            navigationTargets[focusedNodeIndex + 1].focus();
                        }
                        break;
                }
            }
        },

        _keydownHandler: function (panelInfo, e) {
            var keyCode = e.keyCode;

            switch (true) {
                case keyCode === keys.TAB:
                    // Set focus on the content of the accordion container
                    if (panelInfo && panelInfo.widget) {
                        e.preventDefault();
                        if (panelInfo.widget.focus) {
                            panelInfo.widget.focus();
                        } else if (panelInfo.widget.domNode) {
                            panelInfo.widget.domNode.focus();
                        }
                    }
                    break;

                case keyCode === keys.UP_ARROW:
                    this._handleNavigation("up");
                    break;

                case keyCode === keys.DOWN_ARROW:
                    this._handleNavigation("down");
                    break;

                case keyCode === keys.LEFT_ARROW:
                case keyCode === keys.RIGHT_ARROW:
                    if (panelInfo && panelInfo.titleBar && panelInfo.titleBar.titleButton && panelInfo.titleBar.actionsNodeWrapper) {
                        if (e.target === panelInfo.titleBar.titleButton) {
                            panelInfo.titleBar.actionsNodeWrapper.focus();
                        } else if (e.target === panelInfo.titleBar.actionsNodeWrapper) {
                            panelInfo.titleBar.titleButton.focus();
                        }
                    }
                    break;
            }
        },

        _keyUpHandler: function (panelInfo, e) {
            // Collapse/restore accordion container
            if (e.keyCode === keys.SPACE || e.keyCode === keys.ENTER) {
                var that = this;
                if (this.selectionManager) {
                    // Block next child selection since the panel is collapsed/expanded due to a keyboard interaction.
                    this.selectionManager.blockSuccession();
                }
                this._toggleCollapse(panelInfo, e);
            }
        }
    });

    /**
     * Static helper function that returns true if the given container state object includes the specified panel ID
     *
     * @param state {Object} opaque state object obtained from getState
     * @param panelId {string} identifier for panel of interest
     * @static
     */
    AccordionContainer.stateIncludes = function (state, panelId) {
        var index;
        for (index = 0; index < state.length; index += 1) {
            var id = state[index].id || state[index].panelId;
            if (id === panelId) {
                return true;
            }
        }
        return false;
    };

    return AccordionContainer;
});
